import { ArrowLeft, BookOpen } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";

export default function ModeEmploiPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-amber-50">
      <div className="max-w-2xl mx-auto p-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          <h1 className="text-3xl font-bold text-amber-900">
            Mode d'emploi
          </h1>
          <p className="text-sm text-amber-600">
            Comment utiliser l'application Bible Fillion 1920
          </p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                📖 Navigation
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Utilisez le menu (☰) pour accéder à toutes les fonctionnalités</li>
                <li>La Table des Matières vous permet de naviguer entre les livres et chapitres</li>
                <li>Sélectionnez un livre puis un chapitre pour commencer la lecture</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                🔍 Recherche
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Accédez à la Recherche depuis le menu principal</li>
                <li>Tapez un mot ou une expression pour trouver les versets correspondants</li>
                <li>Cliquez sur un résultat pour lire le verset dans son contexte</li>
              </ul>
            </section>

            <section className="bg-amber-100/60 p-4 rounded-lg border border-amber-200">
              <h2 className="text-xl font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-rubric-red" />
                Comprendre les Commentaires Fillion
              </h2>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                Les commentaires contiennent des divisions de plan (<em>I.</em>, <em>1°</em>, <em>1 — 2a</em>), des lemmes latins de la Vulgate et des abréviations savantes (<em>Comp.</em>, <em>et ss.</em>, <em>Atl. arch.</em>). Un guide complet vous explique pas à pas leur fonctionnement.
              </p>
              <Button asChild size="sm" className="bg-rubric-red hover:bg-rubric-red/90 text-white">
                <Link to="/guide-commentaires">Consulter le guide des commentaires</Link>
              </Button>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                📲 Installer l'app sur votre appareil
              </h2>
              <p className="text-sm mb-3">
                Installez Bible Fillion sur votre écran d'accueil pour un accès rapide, en plein écran et même hors-ligne.
              </p>

              <h3 className="font-semibold text-amber-700 mt-4 mb-2">Sur Android (Chrome, Brave, Edge)</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Ouvrez le menu du navigateur (les trois points ⋮)</li>
                <li>Touchez « Installer l'application » ou « Ajouter à l'écran d'accueil »</li>
                <li>Confirmez : l'icône Bible Fillion apparaît sur votre écran d'accueil</li>
                <li>Vous pouvez aussi télécharger l'app officielle depuis le Google Play Store</li>
              </ul>

              <h3 className="font-semibold text-amber-700 mt-4 mb-2">Sur iPhone / iPad (Safari uniquement)</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Touchez l'icône Partager <span className="inline-block px-2 py-0.5 bg-gray-100 rounded border text-xs">↑</span> en bas de Safari</li>
                <li>Faites défiler puis touchez « Sur l'écran d'accueil »</li>
                <li>Touchez « Ajouter » en haut à droite</li>
              </ul>
              <p className="text-xs text-amber-600 italic mt-2 ml-4">
                ⚠️ Sur iPhone, l'installation fonctionne uniquement dans Safari, pas dans Chrome ou Firefox.
              </p>

              <h3 className="font-semibold text-amber-700 mt-4 mb-2">Partager l'app à un proche</h3>
              <p className="text-sm ml-4">
                Vous pouvez partager directement le lien <strong>biblefillion.app</strong> par WhatsApp, SMS ou email.
                Vos proches pourront lire la Bible immédiatement dans leur navigateur, sans rien installer.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                📑 Garde-pages (Premium)
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Sauvegardez votre position de lecture actuelle</li>
                <li>Créez plusieurs garde-pages pour différents passages</li>
                <li>Retrouvez-les facilement dans le menu</li>
                <li>Donnez un titre personnalisé à chaque garde-page</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                🖍️ Surlignages (Premium)
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Sélectionnez du texte pendant la lecture</li>
                <li>Choisissez une couleur de surlignage</li>
                <li>Accédez à tous vos surlignages depuis le menu</li>
                <li>Supprimez un surlignage en cliquant dessus</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                📝 Notes (Premium)
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Ajoutez des notes personnelles sur n'importe quel verset</li>
                <li>Retrouvez toutes vos notes dans le menu</li>
                <li>Modifiez ou supprimez vos notes à tout moment</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                📡 Lecture hors-ligne
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Les chapitres que vous consultez sont automatiquement enregistrés sur votre appareil</li>
                <li>Vous pouvez les relire <strong>sans connexion Internet</strong> à tout moment</li>
                <li>Une notification discrète apparaît quand vous passez en mode hors-ligne</li>
                <li>Pour mettre en cache un livre entier : parcourez ses chapitres une première fois en ligne, ils resteront ensuite disponibles offline</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                🔊 Lecture audio (Premium)
              </h2>
              <p className="text-sm mb-3">
                L'application utilise la synthèse vocale de votre appareil pour lire les chapitres à voix haute.
              </p>
              <p className="text-sm font-semibold text-amber-700 mt-3 mb-2">Sur Android — Si aucune voix française ne fonctionne :</p>
              <ol className="list-decimal list-inside space-y-1 ml-4 text-sm">
                <li>Ouvrez les <strong>Paramètres</strong> de votre téléphone</li>
                <li>Allez dans <strong>Général</strong> → <strong>Synthèse vocale</strong> (ou Accessibilité → Synthèse vocale selon la marque)</li>
                <li>Sélectionnez <strong>Google Text-to-Speech</strong> comme moteur par défaut</li>
                <li>Touchez l'icône ⚙️ à côté de Google → <strong>Installer la voix française</strong></li>
                <li>Revenez dans l'app et testez la lecture</li>
              </ol>
              <p className="text-xs text-amber-600 italic mt-2 ml-4">
                💡 Samsung propose parfois « Samsung Text-to-Speech » par défaut, sans voix française. Préférez « Google Text-to-Speech » pour un meilleur résultat.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                📅 Lectures du Jour
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Consultez les lectures liturgiques quotidiennes</li>
                <li>Les lectures sont mises à jour automatiquement chaque jour à 5h</li>
                <li>Suivez le calendrier liturgique catholique</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-amber-800 mb-3">
                ⭐ Premium
              </h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Accédez à toutes les fonctionnalités avancées</li>
                <li>Paiement sécurisé par carte bancaire</li>
                <li>Activez le Premium depuis le menu principal</li>
              </ul>
            </section>

            <section className="pt-4 border-t">
              <p className="text-center text-sm text-gray-500">
                Bible Fillion 1920 - Édition numérique
              </p>
              <p className="text-center text-xs text-gray-400 mt-2">
                Pour toute question ou assistance, consultez la documentation
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
